import SchedulingQuestion from '~/components/forms/questions/SchedulingQuestion';
import YesnoQuestion from '~/components/forms/questions/YesnoQuestion';
import TextQuestion from '~/components/forms/questions/TextQuestion';
import BigtextQuestion from '~/components/forms/questions/BigtextQuestion';
import CheckboxesQuestion from '~/components/forms/questions/CheckboxesQuestion';
import DropdownQuestion from '~/components/forms/questions/DropdownQuestion';
import RadioQuestion from '~/components/forms/questions/RadioQuestion';
import EmailQuestion from '~/components/forms/questions/EmailQuestion';
import PhoneQuestion from '~/components/forms/questions/PhoneQuestion';
import DisplayTextQuestion from '~/components/forms/questions/DisplayTextQuestion';
import VideoEmbedQuestion from '~/components/forms/questions/VideoEmbedQuestion';
import ThankYouConfirmation from '~/components/forms/questions/ThankYouConfirmation';
import AddonQuestion from '~/components/forms/questions/AddonQuestion';
import AgreeToTermsQuestion from '~/components/forms/questions/AgreeToTermsQuestion';
import {
    ScaleIcon,
    SquaresPlusIcon,
    ArchiveBoxIcon,
    KeyIcon,
    BanknotesIcon,
    AdjustmentsHorizontalIcon,
    VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { EyeSlashIcon } from '@heroicons/react/16/solid';
import ClientAccountQuestion from '~/components/forms/questions/ClientAccountQuestion';
import HiddenQuestion from '~/components/forms/questions/HiddenQuestion';
import PaymentQuestion from '~/components/forms/questions/PaymentQuestion';
import VariablePaymentQuestion from '~/components/forms/questions/VariablePaymentQuestion';
import SelectionSlashIcon from '~/components/icons/SelectionSlashIcon';
import CheckSquareIcon from '~/components/icons/CheckSquareIcon';
import DropDownMenuIcon from '~/components/icons/DropDownMenuIcon';
import SelectButtonIcon from '~/components/icons/SelectButtonIcon';
import InputBoxIcon from '~/components/icons/InputBoxIcon';
import InitialLetterIcon from '~/components/icons/InitialLetterIcon';
import UppercaseIcon from '~/components/icons/UppercaseIcon';
import MailSendIcon from '~/components/icons/MailSendIcon';
import PhoneMobilePhoneIcon from '~/components/icons/PhoneMobilePhoneIcon';
import ReceiptCheckIcon from '~/components/icons/ReceiptCheckIcon';
import CalendarMarkIcon from '~/components/icons/CalendarMarkIcon';
import ContactInfoQuestion from '~/components/forms/questions/ContactInfoQuestion';
import UserCircleSingleIcon from '~/components/icons/UserCircleSingleIcon';
import PackagesQuestion from '~/components/forms/questions/PackagesQuestion';
import UploadSquareIcon from '~/components/icons/UploadSquareIcon';
import FileUploadQuestion from '~/components/forms/questions/FileUploadQuestion';
import DateQuestion from '~/components/forms/questions/DateQuestion';
import DatePickerIcon from '~/components/icons/DatePickerIcon';
import { CUSTOM_QUESTION_CATEGORY, QuestionType } from './Questions.constants';
import { QuestionSchema } from './Questions.types';
import SignatureIcon from '~/components/icons/SignatureIcon';
import SignatureQuestion from '~/components/forms/questions/SignatureQuestion';
import { isSchedulingAnswerValid, parseDateAnswer } from './Questions.utils';
import { BookableResource, EventType, FormQuestion } from '../Api.types';
import ClassSchedulingQuestion from '~/components/forms/questions/ClassSchedulingQuestion';
import ClassLessonIcon from '~/components/icons/ClassLessonIcon';
import GiftIcon from '~/components/icons/GiftIcon';
import { FEATURE_GIFT_CERTIFICATES } from '~/lib/Features';
import GiftCertificateQuestion from '~/components/forms/questions/GiftCertificateQuestion';
import LocationQuestion from '~/components/forms/questions/LocationQuestion';
import { MapPinIcon } from '@heroicons/react/24/outline/index.js';
import InvoiceHeaderQuestion from '~/components/forms/questions/InvoiceHeaderQuestion';
import InvoiceLineItemsQuestion from '~/components/forms/questions/InvoiceLineItemsQuestion';
import InvoiceSummaryQuestion from '~/components/forms/questions/InvoiceSummaryQuestion';
import BillingInfoQuestion from '~/components/forms/questions/BillingInfoQuestion';
import {
    DocumentTextIcon,
    ListBulletIcon,
    CalculatorIcon,
    DocumentChartBarIcon,
    PuzzlePieceIcon,
} from '@heroicons/react/24/outline';
import CustomQuestion from '~/components/forms/questions/CustomQuestion';
import {
    getCustomQuestion,
    getCustomQuestionValidity,
} from '~/lib/CustomQuestions/CustomQuestionRegistry';

function isValidEmail(email) {
    const re = /\S+@\S+\.\S+/;
    return re.test(email);
}

function getEmailError(address) {
    if (address.indexOf('@') > -1) {
        return 'Email address is invalid';
    } else {
        return 'Email address is invalid, must contain @';
    }
}

export const QuestionCategories = [
    'Login',
    'Contact Info',
    'Text',
    'Choice',
    'Scheduling',
    'Selling',
    'Display',
];

function getSchedulingConditionalFields(
    question: FormQuestion,
    schema: QuestionSchema,
    allTypes: Array<EventType> = [],
    allBookableResources: Array<BookableResource> = [],
) {
    const { settings } = question;
    const resourcesWhiteList = settings?.bookableResources;
    const bookableResources = allBookableResources.filter(resource => {
        if (resourcesWhiteList && resourcesWhiteList.length > 0) {
            return resourcesWhiteList.includes(resource.id);
        }
        return true;
    });
    const types = allTypes.filter(type => {
        if (settings?.eventTypes && settings?.eventTypes.length > 0) {
            return settings.eventTypes.indexOf(type.id) > -1;
        } else {
            return true;
        }
    });

    const result = [
        {
            id: 'eventType',
            label: 'Event Type',
            options: types.map(t => t.id + ''),
            optionLabels: types.reduce((map, t) => {
                return { ...map, [t.id]: t.name };
            }, {}),
        },
    ];

    // If we're always picking any available then this doesn't apply,
    //  we don't know until after
    if (
        settings?.pooledAvailability !== 'required' &&
        settings?.pooledAvailability !== 'optional'
    ) {
        result.push({
            id: 'bookableResource',
            label: 'Booking With',
            options: bookableResources.map(t => t.id + ''),
            optionLabels: bookableResources.reduce((map, t) => {
                return { ...map, [t.id]: t.name };
            }, {}),
        });
    }

    return result;
}

/**
 * Also in SchedulerService there is $omitQuestionTypes if you want to
 * exclude any questions from being added by default to admin scheduled appointments
 * ex., anything display related that clients won't answer shouldn't get a
 * default answer
 */
const Questions = {
    [QuestionType.Scheduling]: {
        component: SchedulingQuestion,
        name: 'Appointment',
        category: 'Scheduling',
        icon: CalendarMarkIcon,
        hideExcludePrefill: true,
        noRequired: true,
        alwaysRequired: true,
        noLabel: true,
        excludeFromAnswers: true, // Don't show in answer details
        hideFollowingQuestions: (question, answer) => {
            return !isSchedulingAnswerValid(answer);
        },
        getConditionalFields: getSchedulingConditionalFields,
    },
    [QuestionType.ClassScheduling]: {
        component: ClassSchedulingQuestion,
        name: 'Class Booking',
        category: 'Scheduling',
        icon: ClassLessonIcon,
        hideExcludePrefill: true,
        noRequired: true,
        alwaysRequired: true,
        noLabel: true,
        excludeFromAnswers: true, // Don't show in answer details
        hideFollowingQuestions: (question, answer) => {
            return !isSchedulingAnswerValid(answer);
        },
        getConditionalFields: getSchedulingConditionalFields,
    },
    [QuestionType.AddOn]: {
        component: AddonQuestion,
        name: 'Add-on',
        category: 'Selling',
        icon: SquaresPlusIcon,
        hideExcludePrefill: true,
        // getConditionalFields: (question, schema) => {
        //     const addons = question?.settings?.addons || [];
        //
        //     return [
        //         {
        //             id: 'addonName',
        //             label: 'Add-On',
        //             options: addons.map((a) => a.name),
        //             optionLabels: addons.reduce((map, a) => {
        //                 return {...map, [a.name]: a.name};
        //             }, {}),
        //         },
        //     ];
        // }
    },
    [QuestionType.Packages]: {
        component: PackagesQuestion,
        name: 'Packages',
        category: 'Selling',
        excludeFromAnswers: true,
        hideExcludePrefill: true,
        icon: ArchiveBoxIcon,
    },
    [QuestionType.Payment]: {
        component: PaymentQuestion,
        name: 'Payment',
        category: 'Selling',
        excludeFromAnswers: true,
        hideExcludePrefill: true,
        icon: BanknotesIcon,
        noRequired: true,
    },
    [QuestionType.VariablePayment]: {
        component: VariablePaymentQuestion,
        name: 'Variable Payment',
        category: 'Selling',
        excludeFromAnswers: false,
        hideExcludePrefill: true,
        icon: AdjustmentsHorizontalIcon,
        noRequired: true,
        defaultSettings: {
            minAmount: 0,
            defaultAmount: 50,
            maxAmount: 100,
        },
    },
    [QuestionType.YesNo]: {
        component: YesnoQuestion,
        icon: SelectionSlashIcon,
        name: 'Yes/No',
        category: 'Choice',
        options: ['Yes', 'No'],
    },
    [QuestionType.AgreeToTerms]: {
        component: AgreeToTermsQuestion,
        icon: ScaleIcon,
        hideExcludePrefill: true,
        alwaysRequired: true,
        name: 'Agree To Terms',
        category: 'Choice',
        options: ['Yes', 'No'],
    },
    [QuestionType.Checkboxes]: {
        component: CheckboxesQuestion,
        icon: CheckSquareIcon,
        name: 'Checkboxes',
        category: 'Choice',
        hasOptions: true,
    },
    [QuestionType.Dropdown]: {
        component: DropdownQuestion,
        icon: DropDownMenuIcon,
        name: 'Dropdown',
        category: 'Choice',
        hasOptions: true,
    },
    [QuestionType.Radios]: {
        component: RadioQuestion,
        icon: SelectButtonIcon,
        name: 'Radio',
        category: 'Choice',
        hasOptions: true,
    },
    [QuestionType.Text]: {
        component: TextQuestion,
        icon: InputBoxIcon,
        name: 'Single Line',
        category: 'Text',
        // The numeric variant renders min/max on the input, which the browser
        //  used to enforce for us before we turned native validation off
        getValidationError: (answer, ref, question) => {
            const { numeric, min, max } = question?.settings || {};
            if (!numeric || !answer) {
                return;
            }

            const value = parseFloat(answer);
            if (isNaN(value)) {
                return 'Please enter a number';
            }

            if (min !== undefined && min !== '' && value < parseFloat(min)) {
                return `Must be ${min} or more`;
            }

            if (max !== undefined && max !== '' && value > parseFloat(max)) {
                return `Must be ${max} or less`;
            }
        },
    },
    [QuestionType.BigText]: {
        component: BigtextQuestion,
        icon: InitialLetterIcon,
        name: 'Paragraph',
        category: 'Text',
    },
    [QuestionType.Date]: {
        component: DateQuestion,
        icon: DatePickerIcon,
        name: 'Date',
        category: 'Text',
        forwardsRef: true,
        getValidationError: (answer, ref) => {
            // An incomplete date reports an empty value, so ask the input
            if (ref?.hasBadInput?.()) {
                return 'Please enter a valid date';
            }
        },
    },
    [QuestionType.Location]: {
        component: LocationQuestion,
        icon: MapPinIcon,
        name: 'Location',
        category: 'Text',
    },
    [QuestionType.Display]: {
        component: DisplayTextQuestion,
        icon: UppercaseIcon,
        hideExcludePrefill: true,
        excludeFromAnswers: true,
        name: 'Display Text',
        category: 'Display',
        noRequired: true,
        dontDefaultLabelToType: true,
    },
    [QuestionType.VideoEmbed]: {
        component: VideoEmbedQuestion,
        icon: VideoCameraIcon,
        hideExcludePrefill: true,
        excludeFromAnswers: true,
        name: 'Video Embed',
        category: 'Display',
        noRequired: true,
        dontDefaultLabelToType: true,
        noLabel: true,
    },
    [QuestionType.Hidden]: {
        component: HiddenQuestion,
        icon: EyeSlashIcon,
        hideExcludePrefill: true,
        name: 'URL Parameter',
        category: 'Display',
        noRequired: true,
    },
    [QuestionType.ClientAccount]: {
        component: ClientAccountQuestion,
        icon: KeyIcon,
        name: 'Client Login',
        category: 'Login',
        excludeFromAnswers: true,
        hideExcludePrefill: true,
        noLabel: true,
        hideFollowingQuestions: (question, answer) => {
            return question?.settings?.required && !answer?.isLoggedIn;
        },
    },
    [QuestionType.ContactInfo]: {
        component: ContactInfoQuestion,
        icon: UserCircleSingleIcon,
        name: 'Client Contact Info',
        category: 'Contact Info',
        noRequired: true,
        noLabel: true,
        getValidationError: answer => {
            if (
                !answer.length ||
                (answer[0]?.name || '').trim().indexOf(' ') < 0
            ) {
                return 'First and last name are required';
            }
        },
    },
    [QuestionType.Email]: {
        component: EmailQuestion,
        icon: MailSendIcon,
        name: 'Email',
        category: 'Contact Info',
        getValidationError: answer => {
            if (!!answer && !isValidEmail(answer)) {
                return getEmailError(answer);
            }
        },
    },
    [QuestionType.Upload]: {
        component: FileUploadQuestion,
        icon: UploadSquareIcon,
        hideExcludePrefill: true,
        name: 'File Upload',
        category: 'Choice',
    },
    [QuestionType.Phone]: {
        component: PhoneQuestion,
        icon: PhoneMobilePhoneIcon,
        name: 'Phone Number',
        category: 'Contact Info',
    },
    [QuestionType.ThankYou]: {
        component: ThankYouConfirmation,
        icon: ReceiptCheckIcon,
        name: 'Submission Confirmation',
        category: 'Confirmation',
        noRequired: true,
        noLabel: true,
        hideExcludePrefill: true,
    },
    [QuestionType.Signature]: {
        component: SignatureQuestion,
        icon: SignatureIcon,
        name: 'Signature',
        category: 'Legal',
        noRequired: true,
        alwaysRequired: true,
    },
    [QuestionType.GiftCertificate]: {
        component: GiftCertificateQuestion,
        name: 'Gift Certificate',
        category: 'Selling',
        excludeFromAnswers: true,
        hideExcludePrefill: true,
        alwaysRequired: true,
        noRequired: true,
        icon: GiftIcon,
        feature: FEATURE_GIFT_CERTIFICATES,
        componentHandlesErrors: true,
        getValidationError: answer => {
            if (!!answer && answer.amount < 0) {
                return 'An amount is required';
            }
        },
    },
    [QuestionType.InvoiceHeader]: {
        component: InvoiceHeaderQuestion,
        name: 'Invoice Header',
        category: 'Invoice',
        icon: DocumentTextIcon,
        noRequired: true,
        hideExcludePrefill: true,
        excludeFromAnswers: true,
        noLabel: true,
    },
    [QuestionType.InvoiceLineItems]: {
        component: InvoiceLineItemsQuestion,
        name: 'Line Items',
        category: 'Invoice',
        icon: ListBulletIcon,
        noRequired: true,
        hideExcludePrefill: true,
        excludeFromAnswers: true,
        noLabel: true,
    },
    [QuestionType.InvoiceSummary]: {
        component: InvoiceSummaryQuestion,
        name: 'Invoice Summary',
        category: 'Invoice - Required',
        icon: CalculatorIcon,
        noRequired: true,
        hideExcludePrefill: true,
        excludeFromAnswers: true,
        noLabel: true,
        cannotDelete: true,
    },
    /**
     * Every question a business wrote themselves shares this one entry - which
     *  of their definitions a question uses is in its settings. That keeps the
     *  registry a fixed shape, so the dozen places that key off it carry on
     *  working, and keeps a business's code out of the bundle.
     *
     * The Add Element panel lists their definitions separately and adds them
     *  all as this type.
     */
    [QuestionType.Custom]: {
        component: CustomQuestion,
        icon: PuzzlePieceIcon,
        name: 'Custom',
        category: CUSTOM_QUESTION_CATEGORY,
        // The panel lists the business's own definitions instead
        hideFromAddPanel: true,
        // A definition's own flags decide whether it has a label to set or an
        //  answer to require - see EditQuestionForm
        hideExcludePrefill: true,
        getValidationError: (answer, ref, question) => {
            // What the question's own code last said about its answer. It
            //  reports whenever it likes; validation happens all at once.
            //  Checked before anything else, so a question that reported a
            //  problem blocks the form whether or not its definition happens
            //  to have reached the registry
            const validity = getCustomQuestionValidity(question?.id);

            if (validity && !validity.isValid) {
                return validity.message || 'Please check this';
            }

            const definition = getCustomQuestion(
                question?.settings?.customQuestionId,
            );

            const isEmpty =
                answer === null ||
                answer === undefined ||
                answer === '' ||
                (Array.isArray(answer) && !answer.length);

            if (definition?.flags?.alwaysRequired && isEmpty) {
                return 'This is required';
            }
        },
    },
    [QuestionType.BillingInfo]: {
        component: BillingInfoQuestion,
        name: 'Billing Info',
        category: 'Invoice - Required',
        icon: DocumentChartBarIcon,
        noRequired: true,
        hideExcludePrefill: true,
        excludeFromAnswers: true,
        noLabel: true,
        cannotDelete: true,
    },
};

export default Questions;
